/**
 * Trustico® Configuration File serializer and parser.
 *
 * The Configuration File is a JSON document customers can save at any
 * point after starting an issuance - mid-validation or after success -
 * and drop back onto the Step 1 page to RESUME that exact issuance or
 * reuse the same Automatic Certificate Management Environment (ACME)
 * account for a future SSL Certificate. Schema-versioned so future
 * format changes can migrate older files.
 *
 * Schema v2 fields :
 *   schemaVersion           - integer, currently 2
 *   generatedAt             - ISO-8601 timestamp
 *   serverId                - id from config.ACME_SERVERS (e.g. "trustico-dv")
 *   serverUrl               - full ACME service Uniform Resource Locator (URL)
 *   eab                     - { kid, hmacKey }, REQUIRED. Both halves
 *                             must be present, non-empty and within
 *                             the EAB length bounds : exportConfigFile
 *                             and parseConfigFile each enforce it, so
 *                             a file this tool writes always re-imports.
 *   accountKeyPem           - Privacy-Enhanced Mail (PEM), Public-Key
 *                             Cryptography Standards Number 8 (PKCS#8) form
 *   accountUrl              - URL returned by the ACME server at registration
 *   orderUrl                - OPTIONAL (2026-08-14 operator ruling :
 *                             account reuse must be reachable) :
 *                             in-progress / completed order URL. When
 *                             present it is the resume token - with the
 *                             account key and this URL the server can
 *                             reattach to the exact order at Sectigo®
 *                             (no duplicate order) - and Step 2 offers
 *                             BOTH resume and issue-new-with-this-
 *                             account. When absent, Step 2 enters
 *                             account-reuse mode directly. The SSL
 *                             Certificate key fields below stay
 *                             REQUIRED either way (every
 *                             file this tool exports carries them, so
 *                             "optional orderUrl" never means a
 *                             key-less file) - on a reuse submission
 *                             the browser simply does not SEND the
 *                             saved key (a new order gets a freshly
 *                             generated one ; see the keepOrderPin
 *                             gate in Step2Configure).
 *   certKeyTypeId           - id from config.KEY_TYPES (e.g. "rsa-2048")
 *   certPrivateKeyPem       - PEM PKCS#8 of the SSL Certificate's private key
 *   domains                 - string[] of domain identifiers from the order
 *   certificate (optional)  - { leaf, intermediates, fullChain } from the
 *                             most recent issuance
 *
 * The only piece that cannot be re-derived from Sectigo® is the SSL
 * Certificate private key - everything else (order status, challenges,
 * the issued certificate) is fetched fresh from Sectigo® using the
 * account key + orderUrl when the file is dropped to resume.
 */

import {
  CONFIG_FILE_SCHEMA_VERSION,
  EAB_MIN_LENGTH,
  EAB_MAX_LENGTH,
  MAX_DOMAINS_PER_CERTIFICATE,
  MAX_PEM_LENGTH,
  MAX_URL_LENGTH,
  MAX_DOMAIN_LENGTH,
  MAX_SERVER_ID_LENGTH,
  MAX_KEY_TYPE_ID_LENGTH,
} from "../../shared/issuance/config.js";

// Per-field byte caps moved to src/shared/issuance/config.js so the
// server-side validateInputs can apply the same limits. See
// the JSDoc on those exports for the rationale.

/**
 * Serialize an issuance state (in-progress or complete) to a
 * Configuration File string.
 *
 * @param {object} args
 * @param {string} args.serverId
 * @param {string} args.serverUrl
 * @param {{kid: string, hmacKey: string}} args.eab
 * @param {string} args.accountKeyPem
 * @param {string} args.accountUrl
 * @param {string} args.orderUrl - REQUIRED here (see requiredKeys
 *   below), although the schema note above marks the field optional :
 *   that describes what parseConfigFile accepts, and every file this
 *   function writes carries the order URL.
 * @param {string} args.certKeyTypeId
 * @param {string} args.certPrivateKeyPem
 * @param {string[]} args.domains
 * @param {{leaf: string, intermediates: string, fullChain: string}} [args.certificate]
 * @returns {string} JSON text suitable for file download
 */
export function exportConfigFile(args) {
  // Every key below must be present AND non-empty : exportConfigFile
  // fails loud rather than writing a file parseConfigFile would reject
  // on re-import.
  //
  // The empty-string / empty-array half of that check is deliberate,
  // not decorative. parseConfigFile rejects "" for every one of these
  // string fields and rejects an empty `domains` array, so a presence-
  // only guard here would happily write a file that fails at the drop
  // zone - and the customer discovers the corruption only when they
  // try to resume. This is the generalisation of the eab.kid /
  // eab.hmacKey block below, which the 2026-07-04 CI review asked for
  // one field at a time ; closing it for the whole list means a new
  // required field cannot reopen the same hole. Unreachable from any
  // real path today (getConfigurationData refuses to answer until
  // accountKeyPem, certKeyPem and orderUrl are all set), so this is a
  // fail-loud assertion.
  //
  // Should it ever fire, the customer reads it. Both callers (save in
  // useConfigFileSave and handleDownloadConfigFile in Step4Download)
  // show a failure that is not an ApiError behind a sentence of their
  // own, "The configuration file could not be built. ", so each
  // message thrown in this function is only the CAUSE (operator ruling
  // 2026-10-02) : a short full sentence ending in a full stop. A
  // message that named the failed build itself would say it twice in
  // one Alert. This is the prefix-plus-cause shape parseConfigFile
  // follows for Step1Service (operator ruling 2026-08-27).
  const requiredKeys = [
    "serverId",
    "serverUrl",
    "eab",
    "accountKeyPem",
    "accountUrl",
    "orderUrl",
    "certKeyTypeId",
    "certPrivateKeyPem",
    "domains",
  ];
  for (const k of requiredKeys) {
    const v = args[k];
    if (
      v === undefined ||
      v === null ||
      v === "" ||
      (Array.isArray(v) && v.length === 0)
    ) {
      throw new Error("The " + k + " field is missing.");
    }
  }
  // 2026-07-04 CI review 4881841446 observation #5 : the presence
  // check above verifies args.eab exists but not that its members are
  // non-empty strings. Unreachable from any real path today
  // (/configuration always supplies both), but an empty kid / hmacKey
  // would export a Configuration File that parseConfigFile rejects on
  // re-import - the customer discovers the corruption only when they
  // try to resume. Failing loud at EXPORT time turns that latent
  // round-trip break into an immediate, actionable error.
  if (
    typeof args.eab.kid !== "string" ||
    args.eab.kid.length === 0 ||
    typeof args.eab.hmacKey !== "string" ||
    args.eab.hmacKey.length === 0
  ) {
    throw new Error(
      "The External Account Binding (EAB) credentials are missing."
    );
  }
  const body = {
    schemaVersion: CONFIG_FILE_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    serverId: args.serverId,
    serverUrl: args.serverUrl,
    eab: { kid: args.eab.kid, hmacKey: args.eab.hmacKey },
    accountKeyPem: args.accountKeyPem,
    accountUrl: args.accountUrl,
    orderUrl: args.orderUrl,
    certKeyTypeId: args.certKeyTypeId,
    certPrivateKeyPem: args.certPrivateKeyPem,
    domains: args.domains.slice(),
  };
  if (args.certificate) {
    body.certificate = {
      leaf: args.certificate.leaf,
      intermediates: args.certificate.intermediates,
      fullChain: args.certificate.fullChain,
    };
  }
  return JSON.stringify(body, null, 2);
}

/**
 * Parse and validate a Configuration File JSON string. Throws with a
 * customer-friendly error message on any structural problem.
 *
 * @param {string} text - file contents
 * @returns {object} parsed Configuration File body
 */
export function parseConfigFile(text) {
  // These customer-facing messages are plain sentences (operator
  // ruling 2026-08-27). Step1Service prefixes them with a sentence of
  // its own ("The configuration file could not be loaded."), so each
  // one here must stand as a full sentence ending in a full stop, or
  // the Alert reads as two registers in one box.
  if (typeof text !== "string" || text.trim().length === 0) {
    throw new Error("The configuration file is empty.");
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error(
      "The configuration file is not valid JSON (" +
        (e.message || "parse error") +
        ")."
    );
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("The configuration file is not a JSON object.");
  }
  if (parsed.schemaVersion !== CONFIG_FILE_SCHEMA_VERSION) {
    throw new Error(
      "Configuration file schema version " +
        parsed.schemaVersion +
        " is not supported by this version of the tool (expected " +
        CONFIG_FILE_SCHEMA_VERSION +
        ")."
    );
  }
  // Per-field type + non-empty validation, paired with a per-field
  // length cap from the constants above. The caps catch the case
  // where a malicious or corrupted file slips an oversized string
  // past the 1 Megabyte (MB) body-parser limit (e.g. one PEM field
  // padded to 800 Kilobytes (KB)) while everything else is small.
  //
  // Unknown keys are IGNORED rather than rejected, which is what lets
  // a file written by an older build of this tool keep loading : the
  // schema below is what must be present, not an exhaustive list of
  // what may appear.
  //
  // orderUrl is NOT in this required list (2026-08-14 operator
  // ruling : make account reuse reachable). Requiring it made the
  // account-reuse-for-a-new-SSL-Certificate branch unreachable from
  // any real file - every drop entered resume mode locked to the old
  // order, while shipped copy promised the file worked "for any later
  // reissue". It is validated below as an OPTIONAL field (accepted
  // when absent, null or empty ; type-checked and capped when a
  // non-empty value is present).
  const requiredStrings = [
    { key: "serverId", max: MAX_SERVER_ID_LENGTH },
    { key: "serverUrl", max: MAX_URL_LENGTH },
    { key: "accountKeyPem", max: MAX_PEM_LENGTH },
    { key: "accountUrl", max: MAX_URL_LENGTH },
    { key: "certKeyTypeId", max: MAX_KEY_TYPE_ID_LENGTH },
    { key: "certPrivateKeyPem", max: MAX_PEM_LENGTH },
  ];
  for (const { key, max } of requiredStrings) {
    if (typeof parsed[key] !== "string" || parsed[key].length === 0) {
      throw new Error(
        "The configuration file field '" + key + "' is missing or is not text."
      );
    }
    if (parsed[key].length > max) {
      throw new Error(
        "The configuration file field '" +
          key +
          "' is longer than the maximum of " +
          max +
          " characters."
      );
    }
  }
  // OPTIONAL orderUrl (2026-08-14 operator ruling, see the
  // requiredStrings note above). When a non-empty value is present it
  // gets the same string + cap validation the required URL fields
  // get ; absent / null / empty means account-only reuse.
  if (
    parsed.orderUrl !== undefined &&
    parsed.orderUrl !== null &&
    parsed.orderUrl !== ""
  ) {
    if (typeof parsed.orderUrl !== "string") {
      throw new Error(
        "The configuration file field 'orderUrl' must be text when it is present."
      );
    }
    if (parsed.orderUrl.length > MAX_URL_LENGTH) {
      throw new Error(
        "The configuration file field 'orderUrl' is longer than the maximum of " +
          MAX_URL_LENGTH +
          " characters."
      );
    }
  }
  if (
    !parsed.eab ||
    typeof parsed.eab.kid !== "string" ||
    typeof parsed.eab.hmacKey !== "string"
  ) {
    throw new Error(
      "The External Account Binding (EAB) credentials in the configuration file are missing or malformed."
    );
  }
  // Mirror the EAB length caps used by validateInputs in
  // src/server/services/issuance/validation.js
  // (EAB_MIN_LENGTH / EAB_MAX_LENGTH from config.js). PR #76 review
  // low finding : the previous check bounded only the MAXIMUM, so an
  // empty-string kid / hmacKey passed here and was caught later by
  // the server. Rejecting the too-short case up front gives the
  // clearer message at the drop site. Without this, a file with
  // "eab":{"kid":"","hmacKey":""} would reach Step 2 before failing.
  // Readable labels for the two EAB fields (kid / hmacKey) so the
  // customer-facing message reads naturally rather than echoing the
  // raw JSON key.
  const EAB_FIELD_LABELS = {
    kid: "key identifier",
    hmacKey: "Hash-Based Message Authentication Code (HMAC) key",
  };
  for (const field of ["kid", "hmacKey"]) {
    const v = parsed.eab[field];
    if (v.length < EAB_MIN_LENGTH || v.length > EAB_MAX_LENGTH) {
      throw new Error(
        "The External Account Binding (EAB) " +
          EAB_FIELD_LABELS[field] +
          " in the configuration file must be between " +
          EAB_MIN_LENGTH +
          " and " +
          EAB_MAX_LENGTH +
          " characters."
      );
    }
  }
  if (!Array.isArray(parsed.domains) || parsed.domains.length === 0) {
    throw new Error("The configuration file has no domain names.");
  }
  // Mirror MAX_DOMAINS_PER_CERTIFICATE so the file is rejected here
  // rather than after a wasted server round-trip. Sectigo® also caps
  // identifiers per order and would reject a 1000-domain file later.
  if (parsed.domains.length > MAX_DOMAINS_PER_CERTIFICATE) {
    throw new Error(
      "The configuration file lists more than the maximum of " +
        MAX_DOMAINS_PER_CERTIFICATE +
        " domain names per SSL Certificate."
    );
  }
  for (const d of parsed.domains) {
    if (typeof d !== "string" || d.length === 0) {
      throw new Error(
        "The configuration file domain list contains an entry that is not valid."
      );
    }
    // 2026-06-01 eighth-round code review #3 : apply the length cap
    // to the base form (wildcard "*." stripped) so a legitimate
    // 253-character Fully Qualified Domain Name (FQDN) prefixed with
    // "*." (255 characters) is not wrongly rejected. Pattern-swept
    // with validateInputs in src/server/services/issuance/validation.js.
    const base = d.startsWith("*.") ? d.slice(2) : d;
    if (base.length > MAX_DOMAIN_LENGTH) {
      throw new Error(
        "The configuration file domain list contains an entry longer than " +
          MAX_DOMAIN_LENGTH +
          " characters."
      );
    }
  }
  // Optional `certificate` block : exportConfigFile writes it after a
  // successful issuance, and its SHAPE is validated here so a
  // corrupted / malicious file is rejected at the drop site (PR #76
  // review medium #1, the "stray certificate block").
  //
  // Its three values carry NO length cap of their own (operator ruling
  // 2026-10-02), unlike the fields validated above. Step1Service refuses
  // any file larger than 1 megabyte before it is parsed, which already
  // bounds what this block can hold, and the block is NOT part of the
  // resume payload the browser sends to the server (Step 2 sends only
  // accountKeyPem / accountUrl / certKeyPem / orderUrl), so nothing
  // here ever reaches validateInputs.
  //
  // 2026-07-04 PR #76 CI review finding #7 : these customer-facing
  // messages say "SSL CERTIFICATE", never bare "CERTIFICATE", per the
  // Trustico® style guide ("Certificate" is never written alone in
  // user-facing text). The JSON field name `certificate` and the
  // quoted sub-field names (leaf / intermediates / fullChain) are code
  // identifiers, not prose, and stay as-is.
  if (parsed.certificate !== undefined && parsed.certificate !== null) {
    if (
      typeof parsed.certificate !== "object" ||
      Array.isArray(parsed.certificate)
    ) {
      throw new Error(
        "The SSL Certificate block in the configuration file is not an object."
      );
    }
    for (const field of ["leaf", "intermediates", "fullChain"]) {
      const v = parsed.certificate[field];
      if (v !== undefined && v !== null && typeof v !== "string") {
        throw new Error(
          "The SSL Certificate field '" +
            field +
            "' in the configuration file must be text."
        );
      }
    }
  }
  return parsed;
}
